import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { toast } from "sonner";
import { createSubscription, getConfig, getPlans } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function Plans() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [config, setConfig] = useState(null);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    customer_name: "",
    phone: "",
    address: "",
    sector: "",
    start_date: "",
    referral_code: "",
  });

  useEffect(() => {
    Promise.all([getPlans(), getConfig()]).then(([p, c]) => {
      setPlans(p.plans || []);
      setConfig(c);
      const d = new Date();
      d.setDate(d.getDate() + ((7 - d.getDay()) % 7 || 7)); // next Sunday
      setForm((f) => ({
        ...f,
        sector: (c.sectors || [])[0] || "",
        start_date: d.toISOString().slice(0, 10),
      }));
    });
  }, []);

  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      customer_name: f.customer_name || user.name || "",
      phone: f.phone || user.phone || "",
      address: f.address || (user.saved_addresses?.[0]?.address) || "",
    }));
  }, [user]);

  const chosenPlan = useMemo(() => plans.find((p) => p.id === selected), [plans, selected]);

  const canSubmit =
    chosenPlan &&
    form.customer_name.trim().length >= 2 &&
    form.phone.trim().length >= 6 &&
    form.address.trim().length >= 5 &&
    form.start_date;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await createSubscription({
        customer_name: form.customer_name.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        sector: form.sector || null,
        plan_id: chosenPlan.id,
        start_date: form.start_date,
        referral_code: form.referral_code.trim() || null,
      });
      toast.success(`Subscribed! Code ${res.subscription.sub_code}`);
      navigate("/thank-you", { state: { kind: "subscription", ...res } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not subscribe. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-rw-bg text-rw-ink" data-testid="plans-page">
      <Seo
        title="Weekly Laundry Plans"
        description="Weekly laundry subscription plans in Gurugram from ₹349/week. Basic, Family and Premium tiers with wash & fold, ironing, dry cleaning and priority Sunday pickups. Cancel anytime."
        path="/plans"
      />
      <Nav />
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-20">
        <div className="grid md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-7">
            <div className="overline mb-3">Weekly plans</div>
            <h1 className="font-display font-light text-5xl md:text-7xl tracking-tight leading-[1.02]">
              Never fold<br /> a t-shirt <em className="italic text-rw-blue not-italic">again</em>.
            </h1>
          </div>
          <p className="md:col-span-5 text-lg text-rw-ink2 leading-relaxed">
            Pay once a week, we handle the rest. Perfect for people who love their weekends more than their washing machine.
          </p>
        </div>

        {/* Plans */}
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {plans.map((p, i) => {
            const isMid = i === 1;
            return (
              <div
                key={p.id}
                className={`relative rounded-2xl p-8 border transition-all cursor-pointer ${
                  selected === p.id
                    ? "border-rw-ink bg-rw-ink text-white -translate-y-1"
                    : isMid
                      ? "border-rw-ink/20 bg-rw-bg2 hover:-translate-y-1"
                      : "border-rw-ink/10 bg-rw-bg2 hover:-translate-y-1"
                }`}
                onClick={() => setSelected(p.id)}
                data-testid={`plan-card-${p.id}`}
              >
                {isMid && (
                  <div
                    className={`absolute -top-3 left-8 px-3 py-1 rounded-full text-xs uppercase tracking-widest font-semibold ${
                      selected === p.id ? "bg-rw-blue text-rw-ink" : "bg-rw-ink text-white"
                    }`}
                  >
                    Most popular
                  </div>
                )}
                <div className="overline opacity-70">{p.tagline}</div>
                <h3 className="font-display text-3xl md:text-4xl mt-2">{p.name}</h3>
                <div className="mt-6 flex items-baseline gap-3">
                  <span className="font-display text-5xl">₹{p.weekly_price}</span>
                  <span className={`text-sm ${selected === p.id ? "text-white/60" : "text-rw-ink3"}`}>/ week</span>
                </div>
                <div className={`text-sm mt-1 ${selected === p.id ? "text-rw-blue" : "text-rw-ink2"}`}>
                  Save {p.save_pct}% vs regular <span className="line-through opacity-60">₹{p.regular_price}</span>
                </div>
                <ul className={`mt-6 space-y-2 text-sm ${selected === p.id ? "text-white/90" : "text-rw-ink2"}`}>
                  {p.includes.map((it, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${selected === p.id ? "bg-rw-blue" : "bg-rw-ink"}`} />
                      {it}
                    </li>
                  ))}
                </ul>
                <button
                  className={`mt-8 w-full rounded-full py-2.5 text-sm font-semibold ${
                    selected === p.id
                      ? "bg-white text-rw-ink"
                      : "btn-dark"
                  }`}
                  data-testid={`plan-select-${p.id}`}
                  onClick={(e) => { e.stopPropagation(); setSelected(p.id); }}
                >
                  {selected === p.id ? "Selected" : "Choose plan"}
                </button>
              </div>
            );
          })}
        </div>

        {/* Signup Form */}
        {chosenPlan && (
          <div id="signup" className="mt-16 rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-6 md:p-10" data-testid="plan-signup-form">
            <div className="grid md:grid-cols-12 gap-6 items-end mb-6">
              <div className="md:col-span-8">
                <div className="overline">Subscribe to</div>
                <h2 className="font-display text-3xl md:text-4xl mt-1">{chosenPlan.name} · ₹{chosenPlan.weekly_price}/week</h2>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="text-sm text-rw-ink2 md:col-span-4 md:text-right hover:text-rw-ink"
                data-testid="plan-change-btn"
              >Change plan</button>
            </div>
            <form onSubmit={submit} className="grid md:grid-cols-2 gap-4">
              <label className="block">
                <span className="overline">Name</span>
                <input className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2.5 rw-focus"
                  value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                  placeholder="Your full name" data-testid="sub-input-name" />
              </label>
              <label className="block">
                <span className="overline">Phone (WhatsApp)</span>
                <input className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2.5 rw-focus"
                  value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+91 98xxx xxxxx" data-testid="sub-input-phone" />
              </label>
              <label className="block md:col-span-2">
                <span className="overline">Pickup address</span>
                <textarea rows={2} className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2.5 rw-focus"
                  value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="Flat / building / street" data-testid="sub-input-address" />
              </label>
              <label className="block">
                <span className="overline">Sector / area</span>
                <select className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2.5 rw-focus"
                  value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })}
                  data-testid="sub-input-sector">
                  {(config?.sectors || []).map((s) => (<option key={s} value={s}>{s}</option>))}
                </select>
              </label>
              <label className="block">
                <span className="overline">Start date (Sundays recommended)</span>
                <input type="date" className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2.5 rw-focus"
                  value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  min={new Date().toISOString().slice(0, 10)} data-testid="sub-input-start-date" />
              </label>
              <label className="block md:col-span-2">
                <span className="overline">Referral code (optional)</span>
                <input className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2.5 rw-focus font-mono uppercase"
                  value={form.referral_code} onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })}
                  placeholder="RIZZ-XXXXXX" data-testid="sub-input-referral" />
              </label>
              <div className="md:col-span-2 flex justify-end pt-3">
                <button type="submit" disabled={!canSubmit || submitting}
                  className="btn-dark rounded-full px-6 py-2.5 text-sm font-semibold disabled:opacity-40"
                  data-testid="sub-submit-btn">
                  {submitting ? "Subscribing..." : `Start ${chosenPlan.name} — ₹${chosenPlan.weekly_price}/week`}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="mt-16 grid md:grid-cols-3 gap-6">
          {[
            ["No commitment", "Cancel or pause anytime — no questions asked."],
            ["Same runner", "You get a dedicated pickup person after month one."],
            ["Priority slots", "Subscribers get first pick of weekend slots."],
          ].map(([t, d], i) => (
            <div key={i} className="border-t border-rw-ink/10 pt-6" data-testid={`plans-feature-${i}`}>
              <div className="font-display text-xl">{t}</div>
              <p className="text-sm text-rw-ink2 mt-1">{d}</p>
            </div>
          ))}
        </div>
      </div>

      {/* SEO / long-form content */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-16">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="overline mb-3">About weekly plans</div>
            <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight leading-[1.1]">
              A predictable, hands-off laundry routine for busy homes.
            </h2>
          </div>
          <div className="md:col-span-7 text-rw-ink2 text-sm leading-relaxed space-y-4">
            <p>
              A RIZ N WASH weekly subscription replaces every last laundry decision with a single Sunday habit.
              We arrive at your doorstep at the same time every week, take everything that needs washing,
              ironing or dry cleaning, and return it neatly folded within 48 hours. You do not have to
              schedule anything, chase a runner or worry about missing your slot — the routine simply happens
              in the background.
            </p>
            <p>
              The <strong className="text-rw-ink">Basic</strong> plan covers up to 5 kg of wash and iron every week and is priced at ₹349,
              a 12% discount over the pay-as-you-go rate. The <strong className="text-rw-ink">Family</strong> plan at ₹649 per week is our
              most popular tier: 10 kg wash and iron plus 5 pieces of extra ironing included, saving families in
              Gurugram roughly ₹140 every seven days versus ordering the same services individually. Our
              flagship <strong className="text-rw-ink">Premium</strong> plan bundles 15 kg of wash and iron, 10 extra ironed pieces, two dry-cleaned
              garments each month and a guaranteed 24-hour turnaround, all with a dedicated relationship
              manager who is available on WhatsApp seven days a week.
            </p>
            <p>
              Every subscription is month-to-month with no lock-in. Pause your plan when you travel, cancel
              anytime from your WhatsApp thread and switch tiers up or down whenever your household needs
              change. Subscribers also get first pick of weekend slots, priority customer support and access
              to seasonal add-ons like winter blanket cleaning and monsoon shoe care at member-only pricing.
              If you refer a friend, both of you earn credit that stacks with your subscription savings.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
